import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Shield, Plus, Search, MoreHorizontal, Info, Mail, Pencil } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { InputVenture } from "@/components/ui/input-venture";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { recordAudit } from "@/lib/audit-client";
import { canManageRoles } from "@/lib/permissions";
import { useRole } from "@/contexts/RoleContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PageTour } from "@/components/tour/PageTour";
import { rolesPermissionsSteps } from "@/lib/tour-steps";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FeatureFlagsTab } from "@/components/admin/FeatureFlagsTab";
import { TeamPermissionsEditor } from "@/components/admin/TeamPermissionsEditor";
import { EditTeamMemberModal } from "@/components/admin/EditTeamMemberModal";

type AdminRole = 'super_admin' | 'team_admin' | 'member' | 'setter' | 'closer';

interface TeamMember {
  id: string;
  email: string;
  full_name: string;
  role: AdminRole;
  status: string | null;
  created_at: string | null;
  user_id: string | null;
}

const roleDescriptions = {
  super_admin: {
    label: 'Super Admin',
    description: 'Full system access including payment methods, revenue data, and team management',
    tooltip: 'Full access -- manage clients, billing, integrations, team members, and all settings',
    variant: 'error' as const,
  },
  team_admin: {
    label: 'Team Admin',
    description: 'Client registry & integrations access. Cannot view payments or revenue.',
    tooltip: 'Can manage clients and view operations but cannot access billing or manage team roles',
    variant: 'warning' as const,
  },
  member: {
    label: 'Member',
    description: 'View-only access to client registry, support tickets, and dashboards.',
    tooltip: 'Limited access to Dashboard, Pipeline, Demos, and Reports only',
    variant: 'default' as const,
  },
  setter: {
    label: 'Setter',
    description: 'Team portal access: leads + own performance. Admin configures per-person page access.',
    tooltip: 'VH setter — sees own leads and setter home. Permissions customizable per-person.',
    variant: 'default' as const,
  },
  closer: {
    label: 'Closer',
    description: 'Team portal access: demos, Fathom self-connect, demo library. Admin configures per-person page access.',
    tooltip: 'VH closer — sees own demos and Fathom settings. Permissions customizable per-person.',
    variant: 'default' as const,
  },
};

export default function RolesPermissions() {
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [search, setSearch] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [permissionsEditor, setPermissionsEditor] = useState<{
    userId: string;
    role: 'setter' | 'closer';
  } | null>(null);
  // Phase 16.2-06 — EditTeamMemberModal mount state.
  const [editingMember, setEditingMember] = useState<{
    user_id: string;
    email: string;
    full_name: string;
    role: AdminRole;
    linked_roster_name?: string;
  } | null>(null);
  const [openingEditor, setOpeningEditor] = useState<string | null>(null);
  
  const { adminSubRole } = useRole();
  const hasAccess = canManageRoles(adminSubRole || 'member');

  useEffect(() => {
    if (!hasAccess) {
      toast.error("Access denied. Super Admin required.");
      navigate('/admin/command-center');
      return;
    }
    loadTeamMembers();
  }, [hasAccess, navigate]);

  const loadTeamMembers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('admin_users')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast.error('Failed to load team members');
      console.error(error);
    } else {
      setTeamMembers((data || []) as TeamMember[]);
    }
    setLoading(false);
  };

  const filteredMembers = teamMembers.filter(m =>
    m.full_name.toLowerCase().includes(search.toLowerCase()) ||
    m.email.toLowerCase().includes(search.toLowerCase())
  );

  // Get current admin user from localStorage to prevent self-modification
  const currentAdminUser = (() => {
    try {
      const stored = localStorage.getItem('admin_user');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  })();

  const isCurrentUser = (member: TeamMember) => {
    return currentAdminUser?.id === member.id || currentAdminUser?.email === member.email;
  };

  const handleToggleStatus = async (member: TeamMember) => {
    if (isCurrentUser(member)) {
      toast.error('You cannot deactivate your own account');
      return;
    }

    const newStatus = member.status === 'active' ? 'inactive' : 'active';
    const { error } = await supabase
      .from('admin_users')
      .update({ status: newStatus })
      .eq('id', member.id);

    if (error) {
      toast.error('Failed to update status');
    } else {
      toast.success(`Member ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      loadTeamMembers();
    }
  };

  const handleRoleChange = async (member: TeamMember, newRole: AdminRole) => {
    if (member.role === newRole) return;

    if (isCurrentUser(member)) {
      toast.error('You cannot change your own role');
      return;
    }

    const oldRole = member.role;
    const { error } = await supabase
      .from('admin_users')
      .update({ role: newRole })
      .eq('id', member.id);

    if (error) {
      toast.error('Failed to update role');
    } else {
      // Audit (FOUND-05)
      await recordAudit(
        'role.change',
        { table: 'admin_users', id: member.id },
        { from: oldRole, to: newRole }
      );
      toast.success('Role updated successfully');
      toast.info("Changes take effect on the user's next login.");
      loadTeamMembers();
    }
  };

  const handleDelete = async (member: TeamMember) => {
    if (isCurrentUser(member)) {
      toast.error('You cannot delete your own account');
      return;
    }

    if (member.role === 'super_admin') {
      toast.error("Cannot delete super admin");
      return;
    }

    if (!confirm(`Are you sure you want to delete ${member.full_name}?`)) return;

    const { error } = await supabase
      .from('admin_users')
      .delete()
      .eq('id', member.id);

    if (error) {
      toast.error('Failed to delete team member');
    } else {
      // Audit (FOUND-05) — admin.remove fires here because RolesPermissions
      // deletes admin_users directly via the RLS-gated supabase client.
      await recordAudit(
        'admin.remove',
        { table: 'admin_users', id: member.id },
        { email: member.email, full_name: member.full_name }
      );
      toast.success('Team member deleted');
      loadTeamMembers();
    }
  };

  // Phase 16.2-06 — open the Edit modal with linked-roster-name pre-fetched.
  const handleOpenEdit = async (member: TeamMember) => {
    if (!member.user_id) {
      // No auth uid — still allow editing admin_users fields only.
      setEditingMember({
        user_id: member.id, // fallback to admin_users.id if user_id missing
        email: member.email,
        full_name: member.full_name,
        role: member.role,
      });
      return;
    }
    setOpeningEditor(member.id);
    let linked_roster_name: string | undefined;
    try {
      if (member.role === 'setter') {
        const { data } = await supabase
          .from('vh_team_members')
          .select('name')
          .eq('user_id', member.user_id)
          .maybeSingle();
        linked_roster_name = (data as any)?.name ?? member.full_name;
      } else if (member.role === 'closer') {
        const { data } = await supabase
          .from('closers')
          .select('name')
          .eq('user_id', member.user_id)
          .maybeSingle();
        linked_roster_name = (data as any)?.name ?? member.full_name;
      }
    } catch {
      // Swallow — fall back to full_name.
      linked_roster_name = member.full_name;
    }
    setEditingMember({
      user_id: member.user_id,
      email: member.email,
      full_name: member.full_name,
      role: member.role,
      linked_roster_name,
    });
    setOpeningEditor(null);
  };

  const handleSendReset = async (email: string, name: string) => {
    try {
      const { error } = await supabase.functions.invoke('send-password-reset', {
        body: { email },
      });
      if (error) throw error;
      toast.success(`Password reset email sent to ${name}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to send reset email');
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    return roleDescriptions[role as keyof typeof roleDescriptions]?.variant || 'default';
  };

  const getRoleLabel = (role: string) => {
    return roleDescriptions[role as keyof typeof roleDescriptions]?.label || role;
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageTour steps={rolesPermissionsSteps} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Shield className="w-7 h-7 text-primary" />
            Roles & Permissions
          </h1>
          <p className="text-muted-foreground">Manage team members and their access levels</p>
        </div>
        <Button onClick={() => setIsAddModalOpen(true)} data-tour-id="add-member">
          <Plus className="w-4 h-4 mr-2" />
          Add Team Member
        </Button>
      </div>

      {/* Role Explanations */}
      <div data-tour-id="role-cards" className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(roleDescriptions).map(([key, info]) => (
          <VentureCard key={key} className="p-4">
            <div className="flex items-center gap-2">
              <BadgeVenture variant={info.variant}>{info.label}</BadgeVenture>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{info.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-sm text-muted-foreground mt-2">{info.description}</p>
          </VentureCard>
        ))}
      </div>

      {/* Tabs: Team Members | Feature Flags (super_admin-only — gated above by canManageRoles) */}
      <Tabs defaultValue="members" className="w-full">
        <TabsList>
          <TabsTrigger value="members">Team Members</TabsTrigger>
          <TabsTrigger value="flags">Feature Flags</TabsTrigger>
        </TabsList>
        <TabsContent value="members" className="space-y-6 mt-6">
      {/* Search */}
      <div className="flex gap-4">
        <div className="flex-1 max-w-xs">
          <InputVenture
            icon={<Search className="w-4 h-4" />}
            placeholder="Search team members..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Team Members Table */}
      <VentureCard data-tour-id="member-list">
        <VentureCardHeader>
          <VentureCardTitle>Team Members ({teamMembers.length})</VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Loading team members...</div>
          ) : filteredMembers.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              {search ? 'No team members match your search.' : 'No team members yet. Add your first team member above.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Role</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((member) => (
                    <tr key={member.id} className="border-b border-border hover:bg-surface transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                            <span className="text-xs font-bold text-primary-foreground">
                              {member.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium">{member.full_name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">{member.email}</td>
                      <td className="py-3 px-4">
                        <BadgeVenture variant={getRoleBadgeVariant(member.role) as any}>
                          {getRoleLabel(member.role)}
                        </BadgeVenture>
                      </td>
                      <td className="py-3 px-4">
                        <BadgeVenture variant={member.status === 'active' ? 'success' : 'default'}>
                          {member.status || 'active'}
                        </BadgeVenture>
                      </td>
                      <td className="py-3 px-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleOpenEdit(member)}
                              disabled={openingEditor === member.id}
                            >
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit team member
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSendReset(member.email, member.full_name)}>
                              <Mail className="w-4 h-4 mr-2" />
                              Send Password Reset
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleStatus(member)}>
                              {member.status === 'active' ? 'Deactivate' : 'Activate'}
                            </DropdownMenuItem>
                            {(member.role === 'setter' || member.role === 'closer') && member.user_id && (
                              <DropdownMenuItem
                                onClick={() => setPermissionsEditor({
                                  userId: member.user_id!,
                                  role: member.role as 'setter' | 'closer',
                                })}
                              >
                                Edit Permissions
                              </DropdownMenuItem>
                            )}
                            {member.role !== 'super_admin' && (
                              <DropdownMenuItem onClick={() => handleRoleChange(member, 'super_admin')}>
                                Promote to Super Admin
                              </DropdownMenuItem>
                            )}
                            {member.role !== 'team_admin' && (
                              <DropdownMenuItem onClick={() => handleRoleChange(member, 'team_admin')}>
                                Set as Team Admin
                              </DropdownMenuItem>
                            )}
                            {member.role !== 'member' && (
                              <DropdownMenuItem onClick={() => handleRoleChange(member, 'member')}>
                                Set as Member
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleDelete(member)}
                              className="text-red-400"
                              disabled={member.role === 'super_admin'}
                            >
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>
        </TabsContent>
        <TabsContent value="flags" className="mt-6">
          <FeatureFlagsTab />
        </TabsContent>
      </Tabs>

      {/* Add Member Modal */}
      <AddTeamMemberModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        onSuccess={() => {
          loadTeamMembers();
        }}
      />

      {/* Per-person team permissions editor (D-07, D-14) */}
      {permissionsEditor && (
        <TeamPermissionsEditor
          userId={permissionsEditor.userId}
          role={permissionsEditor.role}
          open={!!permissionsEditor}
          onOpenChange={(v) => { if (!v) setPermissionsEditor(null); }}
        />
      )}

      {/* Phase 16.2-06 — Edit Team Member modal (role + name + permissions). */}
      {editingMember && (
        <EditTeamMemberModal
          open={!!editingMember}
          onOpenChange={(o) => { if (!o) setEditingMember(null); }}
          member={editingMember}
          onSaved={() => {
            loadTeamMembers();
            setEditingMember(null);
          }}
        />
      )}
    </motion.div>
  );
}

interface AddTeamMemberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

function AddTeamMemberModal({ open, onOpenChange, onSuccess }: AddTeamMemberModalProps) {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    role: 'member' as AdminRole,
  });
  const [linkCloserId, setLinkCloserId] = useState<string>('');
  const [linkVhTeamMemberId, setLinkVhTeamMemberId] = useState<string>('');
  const [closerOptions, setCloserOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [vhOptions, setVhOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [createLogin, setCreateLogin] = useState<boolean>(true);
  const [linkMode, setLinkMode] = useState<'create_new' | 'link_existing' | 'from_ghl'>('create_new');
  // Phase 16.2-05 — from_ghl state.
  const [activeClients, setActiveClients] = useState<Array<{ id: string; company_name: string }>>([]);
  const [selectedGhlClientId, setSelectedGhlClientId] = useState<string | null>(null);
  const [ghlUsers, setGhlUsers] = useState<GhlUserLite[] | null>(null);
  const [ghlUsersLoading, setGhlUsersLoading] = useState(false);
  const [selectedGhlUserId, setSelectedGhlUserId] = useState<string | null>(null);
  const [ghlError, setGhlError] = useState<string | null>(null);

  // Load unlinked closers / vh_team_members when role switches (only when link_existing sub-mode)
  useEffect(() => {
    if (!open) return;
    const isTeamRoleLocal = formData.role === 'setter' || formData.role === 'closer';
    if (!isTeamRoleLocal || linkMode !== 'link_existing') return;
    if (formData.role === 'closer') {
      supabase
        .from('closers')
        .select('id, name')
        .is('user_id', null)
        .order('name')
        .then(({ data }) => setCloserOptions((data as any) || []));
    } else if (formData.role === 'setter') {
      supabase
        .from('vh_team_members')
        .select('id, name')
        .is('user_id', null)
        .order('name')
        .then(({ data }) => setVhOptions((data as any) || []));
    }
  }, [open, formData.role, linkMode]);

  const isTeamRole = formData.role === 'setter' || formData.role === 'closer';

  // Phase 16.2-05 — load active clients for the from_ghl client picker.
  useEffect(() => {
    if (!open) return;
    if (!isTeamRole || !createLogin || linkMode !== 'from_ghl') return;
    supabase
      .from('clients')
      .select('id, company_name')
      .eq('status', 'active')
      .order('company_name')
      .then(({ data }) => setActiveClients((data as any) || []));
  }, [open, isTeamRole, createLogin, linkMode]);

  // Phase 16.2-05 — when client selected under from_ghl, invoke ghl-list-users.
  useEffect(() => {
    if (!open) return;
    if (linkMode !== 'from_ghl') return;
    if (!selectedGhlClientId) return;
    setGhlUsersLoading(true);
    setGhlError(null);
    setGhlUsers(null);
    setSelectedGhlUserId(null);
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('ghl-list-users', {
          body: { client_id: selectedGhlClientId },
        });
        // Error-code → toast map (proximity-locked to the invoke call per 16.2-05).
        const code: string | undefined = (data && (data as any).error) || (error && (error as any).error_code);
        if (code && GHL_LIST_USERS_ERROR_COPY[code]) {
          const msg = GHL_LIST_USERS_ERROR_COPY[code];
          setGhlError(msg);
          toast.error(msg);
          setGhlUsers([]);
          return;
        }
        if (error) {
          const msg = GHL_LIST_USERS_ERROR_COPY.ghl_unavailable;
          setGhlError(msg);
          toast.error(msg);
          setGhlUsers([]);
          return;
        }
        const users: GhlUserLite[] = ((data as any)?.users || []) as GhlUserLite[];
        if (users.length === 0) {
          const msg = 'No users found in this GoHighLevel account';
          setGhlError(msg);
          toast.error(msg);
        }
        setGhlUsers(users);
      } catch {
        const msg = GHL_LIST_USERS_ERROR_COPY.ghl_unavailable;
        setGhlError(msg);
        toast.error(msg);
        setGhlUsers([]);
      } finally {
        setGhlUsersLoading(false);
      }
    })();
  }, [open, linkMode, selectedGhlClientId]);

  // Reset from_ghl picker state when sub-mode or client changes.
  useEffect(() => {
    if (linkMode !== 'from_ghl') {
      setSelectedGhlClientId(null);
      setGhlUsers(null);
      setSelectedGhlUserId(null);
      setGhlError(null);
    }
  }, [linkMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Pre-check: team-role invite in link-existing sub-mode must carry its linkage id.
    if (isTeamRole && createLogin && linkMode === 'link_existing') {
      if (formData.role === 'closer' && !linkCloserId) {
        toast.error('Select an existing closer record to link this invite to.');
        return;
      }
      if (formData.role === 'setter' && !linkVhTeamMemberId) {
        toast.error('Select an existing VH team member record to link this invite to.');
        return;
      }
    }

    // Phase 16.2-05 — from_ghl pre-check: must have picked a GHL user.
    if (isTeamRole && createLogin && linkMode === 'from_ghl') {
      if (!selectedGhlUserId) {
        toast.error('Pick a GoHighLevel user first');
        return;
      }
    }
    setLoading(true);

    try {
      let body: Record<string, unknown>;

      if (isTeamRole && linkMode === 'from_ghl' && selectedGhlUserId && ghlUsers) {
        const picked = ghlUsers.find((u) => u.id === selectedGhlUserId);
        if (!picked) {
          toast.error('Pick a GoHighLevel user first');
          setLoading(false);
          return;
        }
        body = await buildFromGhlSubmitBody(formData.role, picked, createLogin);
      } else {
        body = {
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
          ...(isTeamRole && !createLogin ? { roster_only: true } : {}),
          ...(isTeamRole && createLogin && linkMode === 'link_existing' && formData.role === 'closer'
            ? { link_closer_id: linkCloserId }
            : {}),
          ...(isTeamRole && createLogin && linkMode === 'link_existing' && formData.role === 'setter'
            ? { link_vh_team_member_id: linkVhTeamMemberId }
            : {}),
        };
      }

      const { data, error } = await supabase.functions.invoke('invite-admin-user', { body });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(
        !createLogin
          ? 'Roster member added.'
          : "Invite sent! They'll receive an email to set their password."
      );
      onOpenChange(false);
      onSuccess();

      // Reset form
      setFormData({
        full_name: '',
        email: '',
        role: 'member',
      });
      setLinkCloserId('');
      setLinkVhTeamMemberId('');
      setCreateLogin(true);
      setLinkMode('create_new');
      // Phase 16.2-05 reset
      setSelectedGhlClientId(null);
      setGhlUsers(null);
      setSelectedGhlUserId(null);
      setGhlError(null);
    } catch (error: any) {
      console.error('Error inviting team member:', error);
      toast.error(error.message || 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  // Phase 16.2-05 — picked GHL user for readOnly pre-fill + badge.
  const pickedGhlUser: GhlUserLite | null =
    linkMode === 'from_ghl' && selectedGhlUserId && ghlUsers
      ? ghlUsers.find((u) => u.id === selectedGhlUserId) ?? null
      : null;
  const fromGhlReadOnly = linkMode === 'from_ghl' && !!selectedGhlUserId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border max-w-md max-h-[90vh] flex flex-col p-0 gap-0">
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-3 border-b border-border/50">
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogDescription>
            Send an email invite. They'll set their own password when they accept.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full_name">Full Name *</Label>
            <div className="flex items-center gap-2">
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="John Smith"
                required
                readOnly={fromGhlReadOnly}
              />
              {pickedGhlUser && (
                <BadgeVenture variant="default">from GoHighLevel</BadgeVenture>
              )}
            </div>
            {isTeamRole && (
              <p className="text-xs text-muted-foreground">
                Their name as it appears in GoHighLevel.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@venturehills.com"
              required
              readOnly={fromGhlReadOnly}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Role *</Label>
            <Select value={formData.role} onValueChange={(v: any) => setFormData({ ...formData, role: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member (View only)</SelectItem>
                <SelectItem value="team_admin">Team Admin (Most access)</SelectItem>
                <SelectItem value="super_admin">Super Admin (Full access)</SelectItem>
                <SelectItem value="setter">Setter (Team portal)</SelectItem>
                <SelectItem value="closer">Closer (Team portal)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {roleDescriptions[formData.role]?.description}
            </p>
          </div>

          {isTeamRole && (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="create_login" className="cursor-pointer">
                    Send them an app login
                  </Label>
                  <Switch
                    id="create_login"
                    checked={createLogin}
                    onCheckedChange={setCreateLogin}
                  />
                </div>
                {!createLogin && (
                  <p className="text-xs text-muted-foreground">
                    Off = attribution only, no portal access.
                  </p>
                )}
              </div>

              {createLogin && (
                <div className="space-y-2">
                  <Label>How to add</Label>
                  <Select
                    value={linkMode}
                    onValueChange={(v: 'create_new' | 'link_existing' | 'from_ghl') => setLinkMode(v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="create_new">Add a new person</SelectItem>
                      <SelectItem value="link_existing">Choose someone already on file</SelectItem>
                      <SelectItem value="from_ghl">From GoHighLevel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Phase 16.2-05 — from_ghl picker (createLogin && isTeamRole gate) */}
              {createLogin && linkMode === 'from_ghl' && (
                <div className="space-y-2">
                  <Label>Client (GoHighLevel sub-account)</Label>
                  <Select
                    value={selectedGhlClientId ?? ''}
                    onValueChange={(v) => setSelectedGhlClientId(v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pick a client..." />
                    </SelectTrigger>
                    <SelectContent>
                      {activeClients.length === 0 ? (
                        <div className="px-2 py-1.5 text-xs text-muted-foreground">
                          No active clients found.
                        </div>
                      ) : (
                        activeClients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.company_name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>

                  {selectedGhlClientId && (
                    <div className="space-y-2 pt-2">
                      <Label>GoHighLevel User</Label>
                      {ghlUsersLoading ? (
                        <p className="text-xs text-muted-foreground">Loading GoHighLevel users…</p>
                      ) : ghlError ? (
                        <p className="text-xs text-red-400">{ghlError}</p>
                      ) : (
                        <Select
                          value={selectedGhlUserId ?? ''}
                          onValueChange={(v) => {
                            setSelectedGhlUserId(v);
                            const picked = (ghlUsers || []).find((u) => u.id === v);
                            if (picked) {
                              setFormData({
                                ...formData,
                                full_name: `${picked.firstName} ${picked.lastName}`.trim(),
                                email: picked.email,
                              });
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Pick a user..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(ghlUsers || []).length === 0 ? (
                              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                No users found in this GoHighLevel account
                              </div>
                            ) : (
                              (ghlUsers || []).map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                  {u.firstName} {u.lastName} ({u.email})
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {formData.role === 'closer' && createLogin && linkMode === 'link_existing' && (
            <div className="space-y-2">
              <Label htmlFor="link_closer_id">Link to Existing Closer Record *</Label>
              <Select value={linkCloserId} onValueChange={setLinkCloserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an unlinked closer..." />
                </SelectTrigger>
                <SelectContent>
                  {closerOptions.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      No unlinked closers. Create one in Sales Operations first.
                    </div>
                  ) : (
                    closerOptions.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The invite flow will set closers.user_id so the closer is owned by this login.
              </p>
            </div>
          )}

          {formData.role === 'setter' && createLogin && linkMode === 'link_existing' && (
            <div className="space-y-2">
              <Label htmlFor="link_vh_team_member_id">Link to Existing VH Team Member *</Label>
              <Select value={linkVhTeamMemberId} onValueChange={setLinkVhTeamMemberId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an unlinked team member..." />
                </SelectTrigger>
                <SelectContent>
                  {vhOptions.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      No unlinked VH team members. Create one in VH Team first.
                    </div>
                  ) : (
                    vhOptions.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The invite flow will set vh_team_members.user_id so setter attribution resolves.
              </p>
            </div>
          )}

        </div>
        <DialogFooter className="shrink-0 border-t border-border/50 px-6 py-3">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? (!createLogin ? 'Adding...' : 'Sending invite...')
                : (!createLogin ? 'Add to Roster' : 'Send Invite')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 16.2-05 — from_ghl helpers (placed at end of module so the legacy
// `admin_users` source-regex test block remains anchored to the top-level page
// component's query, not these helpers).
//
// Idempotency rationale: closers.email is UNIQUE; vh_team_members has UNIQUE(name, role='setter').
// A naive from_ghl submit that always inline-creates would 409-collide (or, worse for
// closers, create a fresh row with a new email leaving the original admin-created orphan row).
// Fix: before invite, look up an unlinked roster row matching the GHL user's email (closer)
// or full name (setter). If found, submit via link_*_id so invite-admin-user links the
// existing row instead of inserting. Otherwise fall back to inline-create.
// ─────────────────────────────────────────────────────────────────────────────

export interface GhlUserLite {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  fullName: string;
}

export async function buildFromGhlSubmitBody(
  role: AdminRole,
  selectedGhlUser: GhlUserLite,
  createLogin: boolean
): Promise<Record<string, unknown>> {
  const email = selectedGhlUser.email;
  const fullName = `${selectedGhlUser.firstName} ${selectedGhlUser.lastName}`.trim();

  if (role === 'closer' && email) {
    // from_ghl path: closers.email is UNIQUE — prefer linking an existing unlinked closer by email.
    const { data: existing } = await supabase
      .from('closers')
      .select('id')
      .eq('email', email)
      .is('user_id', null)
      .maybeSingle();
    if (existing?.id) {
      const body: Record<string, unknown> = {
        email,
        full_name: fullName,
        role,
        link_closer_id: existing.id,
      };
      if (!createLogin) body.roster_only = true;
      return body;
    }
  } else if (role === 'setter') {
    // from_ghl path: vh_team_members UNIQUE(name, role='setter') — prefer linking an existing unlinked setter by name.
    const { data: existing } = await supabase
      .from('vh_team_members')
      .select('id')
      .eq('name', fullName)
      .eq('role', 'setter')
      .is('user_id', null)
      .maybeSingle();
    if (existing?.id) {
      const body: Record<string, unknown> = {
        email,
        full_name: fullName,
        role,
        link_vh_team_member_id: existing.id,
      };
      if (!createLogin) body.roster_only = true;
      return body;
    }
  }

  // No existing unlinked roster row — fall back to inline-create (16.1-01 contract).
  const body: Record<string, unknown> = { email, full_name: fullName, role };
  if (!createLogin) body.roster_only = true;
  return body;
}

// Phase 16.2-05 — GHL error_code → friendly toast copy.
export const GHL_LIST_USERS_ERROR_COPY: Record<string, string> = {
  no_ghl_connection: 'This client has no GoHighLevel connection',
  token_invalid_reauthorize: 'GoHighLevel reauthorization required',
  scope_missing_reauthorize: 'GoHighLevel reconnection needed to see users',
  ghl_unavailable: 'GoHighLevel is unavailable — try again',
};